import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import type { IntelligenceSummary } from '~/common/types';

const MS_PER_DAY = 86_400_000;

/** Whole days spanned by a summary's clamped schedule-overlap window; null without a window. */
export function overlapDays(conflictWindow: IntelligenceSummary['conflictWindow']): number | null {
  if (!conflictWindow) return null;
  return Math.round((conflictWindow.overlapEndMs - conflictWindow.overlapStartMs) / MS_PER_DAY);
}

/**
 * Pre-edit conflict state for a single summary. Sent as event properties when
 * a date edit is applied: the recompute overwrites the summary in place, so
 * the event is the only durable record of the "before" side.
 */
export function getBeforeSnapshot(summary: IntelligenceSummary) {
  const counts = summary.conflictWindow?.counts;
  return {
    // Optional-chained despite the type: a snapshot failure must never block the edit itself.
    before_recommendation: summary.schedule?.recommendation ?? null,
    before_moratoria_count: counts?.moratoria ?? null,
    before_paving_count: counts?.paving ?? null,
    before_cip_count: counts?.cip ?? null,
    before_conflict_area: summary.conflictArea != null ? Math.round(summary.conflictArea) : null,
    before_overlap_days: overlapDays(summary.conflictWindow),
  };
}

/** Aggregated pre-edit conflict state over the rows the client has cached (bulk edits, dialog open). */
export function aggregateBeforeSnapshots(summaries: IntelligenceSummary[]) {
  const recommendationCounts: Record<string, number> = {};
  let totalConflictArea = 0;
  let totalMoratoria = 0;
  let totalPaving = 0;
  let totalCip = 0;
  let inConflictCount = 0;

  for (const summary of summaries) {
    // Optional-chained despite the type: a snapshot failure must never block the dialog or submit.
    const recommendation = summary.schedule?.recommendation;
    if (recommendation) recommendationCounts[recommendation] = (recommendationCounts[recommendation] ?? 0) + 1;
    totalConflictArea += summary.conflictArea ?? 0;
    const counts = summary.conflictWindow?.counts;
    const rowConflicts = (counts?.moratoria ?? 0) + (counts?.paving ?? 0) + (counts?.cip ?? 0);
    totalMoratoria += counts?.moratoria ?? 0;
    totalPaving += counts?.paving ?? 0;
    totalCip += counts?.cip ?? 0;
    if (rowConflicts > 0) inConflictCount += 1;
  }

  return {
    snapshot_count: summaries.length,
    before_total_conflict_area: Math.round(totalConflictArea),
    before_total_moratoria_count: totalMoratoria,
    before_total_paving_count: totalPaving,
    before_total_cip_count: totalCip,
    before_in_conflict_count: inConflictCount,
    before_recommendation_counts: recommendationCounts,
  };
}

/**
 * Cached rows behind a table selection. Select-all selections can span pages
 * the client never fetched, so they snapshot as empty; consumers report
 * `snapshot_count` alongside so analysis knows the coverage.
 */
export function getSelectionSnapshotRows(
  selection: ServerSideSelectionState,
  getSummaryById: (id: string) => IntelligenceSummary | undefined,
): IntelligenceSummary[] {
  if (selection.mode !== 'partial') return [];
  return selection.selectedIds.map(getSummaryById).filter((s): s is IntelligenceSummary => !!s);
}

/** Table context for the once-per-visit pageview event, built from data the page already loaded. */
export function buildViewedProperties({
  organizationId,
  totalProjects,
  summaries,
  yearFilter,
  myProjectsActive,
  searchLength,
}: {
  organizationId: string;
  totalProjects: number | null;
  summaries: IntelligenceSummary[];
  yearFilter: string | null;
  myProjectsActive: boolean;
  searchLength: number;
}) {
  const aggregates = aggregateBeforeSnapshots(summaries);
  return {
    organization_id: organizationId,
    total_projects: totalProjects,
    loaded_row_count: summaries.length,
    year_filter: yearFilter ? Number(yearFilter) : null,
    my_projects_active: myProjectsActive,
    has_search: searchLength > 0,
    page_in_conflict_count: aggregates.before_in_conflict_count,
    page_recommendation_counts: aggregates.before_recommendation_counts,
  };
}
